import { useCallback, useRef, useEffect, useContext } from 'react';
import { updateNodePosition } from '../api/updateNodePosition';
import AuthContext from '../context/AuthContext';

export function useDrag(nodes, setNodes) {
  const { authFetch } = useContext(AuthContext);
  const draggingNodeId = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startNodeX = useRef(0);
  const startNodeY = useRef(0);

  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  const MIN_X = 0;
  const MIN_Y = 0;
  const MAX_X = 1200; // canvas width
  const MAX_Y = 800; // canvas height

  const NODE_WIDTH = 150;
  const NODE_HEIGHT = 60;

  const handleMouseMove = useCallback((e) => {
    if (!draggingNodeId.current) return;

    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;

    setNodes((prev) =>
      prev.map((n) => {
        if (n.id !== draggingNodeId.current) return n;

        const newX = startNodeX.current + dx;
        const newY = startNodeY.current + dy;

        return {
          ...n,
          x_axis: clamp(newX, MIN_X, MAX_X - NODE_WIDTH),
          y_axis: clamp(newY, MIN_Y, MAX_Y - NODE_HEIGHT),
        };
      }),
    );
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!draggingNodeId.current) return;

    const nodeId = draggingNodeId.current;
    const node = nodesRef.current.find((n) => n.id === nodeId);

    if (node) {
      // Send updated position to backend
      updateNodePosition(authFetch, nodeId, node.x_axis, node.y_axis);
    }

    draggingNodeId.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (e, nodeId, node) => {
      e.preventDefault();

      draggingNodeId.current = nodeId;
      startX.current = e.clientX;
      startY.current = e.clientY;

      startNodeX.current = node.x_axis;
      startNodeY.current = node.y_axis;

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp],
  );

  return handleMouseDown;
}
